<template>
  <div class="features-wrapper">
    <!-- Hero Section -->
    <section class="hero">
      <div class="left">
        <h1>Zeiterfassung mit ChronoFox – optimal für Freelancer</h1>
        <p>
          Spare Zeit, arbeite smarter, und steigere deinen Umsatz mit ChronoFox.
        </p>
        <NuxtLink to="/contact" class="cta-button">Kontakt aufnehmen</NuxtLink>
      </div>

      <div class="right">
        <img
          src="@/assets/website/features/dashboard.png"
          title="ChronoFox Dashboard"
          alt="Dashboard mit linker Navigation, Umsatzübersicht, Kundenliste, Aktivitätenübersicht und digitaler Uhr"
        />
      </div>
    </section>

    <!-- Pain Points Section -->
    <section class="pain-points">
      <h2>Wir kennen deine Herausforderungen als Freelancer</h2>
      <ul>
        <li>Fehlende Übersicht über Projekte und Kunden</li>
        <li>Zeitverschwendung durch manuelle Zeiterfassung</li>
        <li>Schwierigkeiten bei der Nachverfolgung von Budgets und Zeiten</li>
      </ul>
    </section>

    <!-- Features Section -->
    <section class="features">
      <h2>Das bekommst du mit ChronoFox Plus</h2>
      <ul class="features-list">
        <li>
          <div class="icon-container"><i class="fa fa-clock"></i></div>
          <p>Intuitive Zeiterfassung mit digitaler Stempeluhr</p>
        </li>
        <li>
          <div class="icon-container"><i class="fa fa-user"></i></div>
          <p>Kunden- und Projektmanagement</p>
        </li>
        <li>
          <div class="icon-container"><i class="fa fa-chart-line"></i></div>
          <p>Detaillierte Umsatz-Dashboards</p>
        </li>
        <li>
          <div class="icon-container"><i class="fa fa-file-alt"></i></div>
          <p>Berichte im PDF- und CSV-Format</p>
        </li>
        <li>
          <div class="icon-container"><i class="fa fa-tasks"></i></div>
          <p>Verwaltung von Tätigkeiten mit individuellen Stundensätzen</p>
        </li>
        <li>
          <div class="icon-container"><i class="fa fa-file-invoice"></i></div>
          <p>
            PDF- oder CSV-Stundenzettel für deine Kunden und deine Buchhaltung
          </p>
        </li>
      </ul>

      <NuxtLink to="/contact" class="cta-button">
        Erfahre mehr über ChronoFox
      </NuxtLink>
    </section>

    <!-- Always with you on mobile -->
    <section class="mobile-section">
      <div class="left">
        <PhoneMockup :enableHover="true" :rotateDirection="'right'">
          <img
            title="Digitale Uhr auf einem Smartphone"
            src="@/assets/website/startpage/digital-clock-mobile.png"
            alt="Digitale Uhr auf einem Smartphone mit laufender Stoppuhrfunktion"
            class="phone-image"
          />
        </PhoneMockup>
      </div>

      <div class="right">
        <h2>{{ t("website.always_with_you_on_mobile") }}</h2>
        <p>{{ t("website.always_with_you_on_mobile_description") }}</p>

        <div class="redirect-buttons">
          <NuxtLink class="cta-button cta-button--primary" to="/contact">
            {{ t("website.contact_us") }}
          </NuxtLink>
        </div>
      </div>
    </section>

    <!-- Use Cases Section -->
    <section class="use-cases">
      <h2>So hilft ChronoFox dir im Alltag</h2>
      <div class="use-case">
        <i class="fa fa-file-pdf"></i>
        <p>
          Projektreporting leicht gemacht: Zeige deinem Kunden professionelle
          PDF-Berichte.
        </p>
      </div>
      <div class="use-case">
        <i class="fa fa-stopwatch"></i>
        <p>
          Verlorene Minuten zurückholen: Keine vergessenen Arbeitszeiten mehr.
        </p>
      </div>
      <div class="use-case">
        <i class="fa fa-piggy-bank"></i>
        <p>
          Bessere Budgets im Blick: Verfolge deine Einnahmen und Ausgaben in
          Echtzeit.
        </p>
      </div>
      <div class="use-case">
        <i class="fa fa-users"></i>
        <p>
          Kundenmanagement leicht gemacht: Behalte den Überblick über deine
          Kunden.
        </p>
      </div>
    </section>

    <!-- Pricing Section -->
    <section id="pricing" class="pricing-section">
      <PagesPricingFreelancer />
    </section>

    <section class="photo-section">
      <PagesFeaturesPhotoList />
    </section>

    <!-- FAQ Section -->
    <section class="faq">
      <h2>Hast du noch Fragen?</h2>

      <NuxtLink to="/faq" class="cta-button cta-button-outline">
        {{ $t("website.faq.go_to_faq") }}
      </NuxtLink>
    </section>

    <!-- CTA Section -->
    <section class="final-cta">
      <h2>Mach den nächsten Schritt als Freelancer</h2>
      <NuxtLink to="/contact" class="cta-button">
        Jetzt Kontakt aufnehmen
      </NuxtLink>
    </section>
  </div>
</template>

<script setup lang="ts">
const { t, locale } = useI18n();

definePageMeta({
  layout: "website",
});

import logo from "@/assets/logo/chronofox_wortbild_right.png";
useSeoMeta({
  // General
  title:
    t("website.freelancer.time_tracking_for_freelancer") +
    " | " +
    t("app_name"),
  description: t(
    "website.freelancer.simplify_time_tracking_and_reporting_for_your_freelance_work",
  ),
  keywords: `${t("time_tracking")}, ${t("freelancer_key")}, ${t(
    "reporting",
  )}, ${t("project_management")}, ${t("website.professional_timetracking")}`,

  // Open Graph
  ogTitle:
    t("app_name") +
    " - " +
    t("website.freelancer.time_tracking_for_freelancer"),
  ogDescription: t(
    "website.freelancer.simplify_time_tracking_and_reporting_for_your_freelance_work",
  ),
  ogLocale: locale.value ? locale.value : "de",
  ogType: "website",
  ogImage: logo,

  // Twitter (X)
  twitterCard: "summary_large_image",
  twitterTitle:
    t("website.freelancer.time_tracking_for_freelancer") +
    " | " +
    t("app_name"),
  twitterDescription: t(
    "website.freelancer.simplify_time_tracking_and_reporting_for_your_freelance_work",
  ),
  twitterImage: logo,
  twitterSite: "@ChronoFoxApp",
});

const scrollToPricing = () => {
  const pricingSection = document.getElementById("pricing");
  if (!pricingSection) return;
  pricingSection.scrollIntoView({ behavior: "smooth" });
};
</script>

<style scoped>
.features-wrapper {
  font-family: "Arial", sans-serif;
  color: #333;
  line-height: 1.6;
}

.cta-button {
  display: inline-block;
  font-size: 1rem;
  padding: 0.8rem 1.5rem;
  background-color: var(--t-accent);
  border: 2px solid var(--t-accent);
  color: #ffffff;
  border: none;
  border-radius: 5px;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  width: max-content;
}

.cta-button:hover {
  background-color: #0056b3;
}

.cta-button-outline {
  background-color: transparent;
  color: var(--t-accent);
  border: 2px solid var(--t-accent);
}

.cta-button-outline:hover {
  background-color: #007bff;
  color: #ffffff;
}

/* Hero Section */
.hero {
  background: linear-gradient(to right, #f0f4f8, #ffebd9);
  text-align: center;
  padding: 3rem 2.5%;
  border-radius: 8px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
}

.hero .left h1 {
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
  color: #2c3e50;
}

.hero .left p {
  font-size: 1.2rem;
  margin-bottom: 2rem;
  color: #5d6d7e;
}

.hero .right img {
  max-width: 100%;
  height: auto;
  max-height: 400px;
  margin-top: 2rem;
  border-radius: 8px;
}

/* Pain Points Section */
.pain-points {
  background-color: #f9f9f9;
  padding: 2rem 1rem;
  text-align: center;

  display: flex;
  flex-direction: column;
  align-items: center;
}

.pain-points h2 {
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #34495e;
}

.pain-points ul {
  left: 30%;
  list-style: outside;
  padding: 0;
  margin: 0;
  text-align: left;
}

.pain-points li {
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: #7f8c8d;
}

/* Features Section */
.features {
  padding: 4rem 5%;
  text-align: center;
  margin: 2rem auto;
}

.features h2 {
  font-size: 2.4rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 4rem;
}

.features-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

.features-list li {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: linear-gradient(to left, #e4ecfc, #d9e7ff);
  padding: 1rem 1.5rem;
  border-radius: 10px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  transition:
    transform 0.3s,
    box-shadow 0.3s;
}

.features-list li:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.icon-container {
  background-color: var(--t-accent);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  font-size: 1.5rem;
}

.features-list p {
  font-size: 1.2rem;
  color: #34495e;
  margin: 0;
  flex: 1;
}

.features .cta-button {
  margin-top: 4rem;
}

/* Use Cases Section */
.use-cases {
  background-color: #f9f9f9;
  padding: 3rem 5%;

  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.use-cases h2 {
  grid-column: span 2;

  font-size: 2rem;
  margin-bottom: 2rem;
  text-align: center;
  color: #34495e;
}

.use-cases .use-case {
  display: flex;
  gap: 1rem;
  align-items: center;

  border: 2px solid var(--t-accent);
  padding: 1rem;
  border-radius: 5px;
}

.use-cases .use-case i {
  font-size: 2rem;
}

.use-cases .use-case p {
  flex: 1;
  font-size: 1.1rem;
}

/* Pricing Section */

/* Photo List Section */
.photo-section {
  padding: 4rem 5%;
}

/* FAQ Section */
.faq {
  background-color: #ffffff;
  padding: 3rem 1rem;

  text-align: center;
}

.faq h2 {
  font-size: 2rem;
  margin-bottom: 1.5rem;
  text-align: center;
  color: #34495e;
}

/* Final CTA Section */
.final-cta {
  background: linear-gradient(to right, #f0f4f8, #d9e7ff);
  text-align: center;
  padding: 3rem 1rem;
}

.final-cta h2 {
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #2c3e50;
}

.final-cta .cta {
  background-color: #3498db;
  color: white;
  font-size: 1rem;
  padding: 0.8rem 1.5rem;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background 0.3s;
}

.final-cta .cta:hover {
  background-color: #2980b9;
}

/* Mobile Section */
.mobile-section {
  padding: 4rem 5%;
  background-color: var(--t-white);

  display: grid;
  grid-template-columns: 2fr 3fr;
  gap: 2rem;
  align-items: center;
}

.mobile-section .right {
  padding-right: 10%;
}

.mobile-section .right h2 {
  font-size: 2.5rem;
  margin-bottom: 20px;
}

.mobile-section .right p {
  font-size: 1.2rem;
  text-align: justify;
}

.mobile-section .right .redirect-buttons {
  margin-top: 2rem;
}

.mobile-section .left {
  display: flex;
  justify-content: center;
  align-items: center;
}

.mobile-section :deep(.phone-image) {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

@media screen and (max-width: 1000px) {
  .mobile-section {
    grid-template-columns: 1fr;
    padding: 4rem 2rem;
  }
}

/* Responsive Styles */
@media (max-width: 768px) {
  .hero {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .hero img {
    display: none;
  }

  .pain-points ul {
    left: 0;
    padding: 0 5%;
  }

  .features-list {
    grid-template-columns: 1fr;
  }

  .use-cases {
    grid-template-columns: 1fr;
  }

  .photo-list {
    grid-template-columns: 1fr;
    gap: 50px;
  }

  .use-cases {
    display: flex;
    flex-direction: column;
  }

  .use-case {
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
  }
}
</style>
